// One MM account (SM-564 / SM-565): identity header, a page-level
// currency filter, and three tabs — Trading, Balances, Runs.
//
// The file is `accounts_.$id` (trailing underscore) so the route is a
// SIBLING of the accounts list, not a child rendered inside it: the
// list has no <Outlet/> and would otherwise have to grow one.
//
// URL state: tab, section, currency, runs filter and page all live in
// the search params, so a deep link and browser back/forward restore
// the exact view. The list's own search (`q`) rides along so the
// breadcrumb returns to the filtered list the operator came from.
import * as React from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AccountBalancesTab } from "@/components/accounts/AccountBalancesTab";
import { AccountRunsTab } from "@/components/accounts/AccountRunsTab";
import { AccountTradingTabs } from "@/components/accounts/AccountTradingTabs";
import { CopyableID } from "@/components/runs/PresetIdentity";
import { ModeBadge } from "@/components/runs/RunStateTabs";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, SectionCard } from "@/components/ui/Card";
import { CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { Skeleton } from "@/components/ui/Skeleton";
import { useI18n } from "@/i18n/I18nProvider";
import { strategyNameLabel } from "@/i18n/strategyCopy";
import { APIError } from "@/lib/api";
import {
  ACCOUNT_TABS,
  ALL_CURRENCIES,
  isAccountTab,
  isRunFilter,
  isTradingSection,
  normalizeCurrencyFilter,
  orderCurrencies,
  type AccountTab,
  type CurrencyFilter,
  type RunFilter,
  type TradingSection,
} from "@/lib/accountDetail";
import { fetchAccountDetail, fetchMyPermissions, keys } from "@/lib/queries";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";

// Every field OPTIONAL so a bare <Link to="/accounts/$id"> type-checks;
// validateSearch normalizes and the component reads concrete values.
interface AccountSearch {
  tab?: AccountTab;
  section?: TradingSection;
  currency?: string;
  runs?: RunFilter;
  page?: number;
  /** The accounts list's search box, carried so the breadcrumb returns to it. */
  q?: string;
}

export const Route = createFileRoute("/_app/accounts_/$id")({
  // SM-563 — re-read every section on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.accountDetail),
  validateSearch: (search: Record<string, unknown>): AccountSearch => ({
    tab: isAccountTab(search.tab) ? search.tab : "trading",
    section: isTradingSection(search.section) ? search.section : "orders",
    // The known set is not available at validate time; the component
    // re-normalizes against the server's list once it has answered.
    currency: normalizeCurrencyFilter(search.currency, undefined),
    runs: isRunFilter(search.runs) ? search.runs : "all",
    page: typeof search.page === "number" && search.page > 0 ? Math.floor(search.page) : 0,
    q: typeof search.q === "string" && search.q ? search.q : undefined,
  }),
  component: AccountDetailPage,
});

function AccountDetailPage() {
  const { t, formatDateTime } = useI18n();
  const { id } = Route.useParams();
  const rawSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const detailQ = useQuery({
    queryKey: keys.accountDetail(id),
    queryFn: () => fetchAccountDetail(id),
    // Poll the header while a bot is on the desk: status, active runs
    // and the legacy balance figures move.
    refetchInterval: (q) => ((q.state.data?.account.active_runs?.length ?? 0) > 0 ? 5_000 : false),
  });
  const permissionsQ = useQuery({
    queryKey: keys.myPermissions(),
    queryFn: fetchMyPermissions,
    staleTime: 60_000,
    retry: 1,
  });
  const permissions = permissionsQ.data?.permissions ?? [];

  const detail = detailQ.data;
  const account = detail?.account;
  const currencies = orderCurrencies(detail?.currencies ?? []);
  const search = {
    tab: rawSearch.tab ?? "trading",
    section: rawSearch.section ?? "orders",
    currency: normalizeCurrencyFilter(rawSearch.currency, detail ? currencies : undefined) as CurrencyFilter,
    runs: rawSearch.runs ?? "all",
    page: rawSearch.page ?? 0,
    q: rawSearch.q,
  };
  const live = (account?.active_runs?.length ?? 0) > 0 || !!account?.active_run_id;

  function setSearch(patch: Partial<AccountSearch>) {
    // Any change of tab, section, filter or currency starts paging over.
    navigate({ search: (prev) => ({ ...prev, page: 0, ...patch }), replace: true });
  }

  const notFound = detailQ.error instanceof APIError && detailQ.error.status === 404;

  return (
    <div className="space-y-5">
      <div className="text-2xs uppercase tracking-widest t-mute">
        <Link to="/accounts" search={{ q: search.q }} className="hover:t-emph">
          {t("accounts.title")}
        </Link>
        {" / "}
        <span className="text-t-1">{account ? accountName(account.display_name, account.email, id) : id}</span>
      </div>

      {!notFound && <DataFreshness query={detailQ} />}

      {notFound && (
        <Card className="p-6">
          <h1 className="page-title">{t("accountDetail.notFound")}</h1>
          <p className="mt-2 text-sm t-mute">{t("accountDetail.notFoundHint")}</p>
          <div className="mt-4">
            <Link to="/accounts" search={{ q: search.q }}>
              <Button type="button" variant="ghost">
                {t("accountDetail.backToList")}
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {!notFound && detailQ.isLoading && <Skeleton lines={5} />}

      {account && (
        <>
          <header className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="page-title flex flex-wrap items-center gap-2">
                  <span className="truncate">{accountName(account.display_name, account.email, id)}</span>
                  <StatusBadge status={account.status} />
                </h1>
                <p className="page-subtitle">{account.email}</p>
              </div>
              <div className="flex flex-col items-start gap-1 md:items-end">
                <CopyableID value={account.user_id} label={t("accounts.userId")} />
                {/* SM-786: which broker account every figure below is
                    read for. Absent while attribution is off, and then
                    the header is exactly as before. */}
                {account.broker_account_id && (
                  <CopyableID value={account.broker_account_id} label={t("accounts.brokerAccount")} />
                )}
                {account.broker_id && <CopyableID value={account.broker_id} label={t("accounts.brokerId")} />}
                {account.funding_account_code && (
                  <CopyableID value={account.funding_account_code} label={t("accountDetail.header.reserve")} />
                )}
                {account.active_run_id && (
                  <CopyableID value={account.active_run_id} label={t("history.col.run")} />
                )}
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <HeaderCell label={t("accountDetail.header.status")}>
                <StatusBadge status={account.status} />
              </HeaderCell>
              <HeaderCell label={t("accountDetail.header.reserve")}>
                {account.funding_account_code ? (
                  <span className="mono break-all text-xs">
                    {account.funding_account_code}{" "}
                    <Badge tone={account.funding_link_status === "LINKED" ? "yes" : "warn"}>
                      {account.funding_link_status ?? "—"}
                    </Badge>
                  </span>
                ) : (
                  <span className="t-mute">{t("accounts.linkUnlinked")}</span>
                )}
              </HeaderCell>
              <HeaderCell label={t("accountDetail.header.activeRuns")}>
                {account.active_runs && account.active_runs.length > 0 ? (
                  <ul className="space-y-1">
                    {account.active_runs.map((run) => (
                      <li key={run.run_id} className="flex flex-wrap items-center gap-1.5">
                        <ModeBadge mode={String(run.trading_mode)} />
                        <StatusBadge status={String(run.status)} />
                        <Link
                          to="/runs/$id"
                          params={{ id: run.run_id }}
                          className="text-accent hover:underline"
                          title={run.run_id}
                        >
                          {run.preset_name || run.run_id.slice(0, 12)}
                        </Link>
                        {run.strategy && <span className="t-mute">{strategyNameLabel(run.strategy, t)}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="t-mute">{t("accounts.idle")}</span>
                )}
              </HeaderCell>
              <HeaderCell label={t("common.updated")}>
                <span className="mono">{formatDateTime(detail.updated_at)}</span>
              </HeaderCell>
            </dl>
          </header>

          <SectionCard
            title={
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("accountDetail.tabsAria")}>
                  {ACCOUNT_TABS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={search.tab === tab}
                      className={`page-tab ${search.tab === tab ? "is-active" : ""}`}
                      onClick={() => setSearch({ tab })}
                    >
                      {t(`accountDetail.tab.${tab}` as const)}
                    </button>
                  ))}
                </div>
              </div>
            }
            actions={
              <CurrencyFilterTabs
                value={search.currency}
                currencies={currencies}
                onChange={(currency) => setSearch({ currency })}
              />
            }
          >
            <p className="mb-3 text-2xs t-mute">{t("accountDetail.currencyNote")}</p>

            {search.tab === "trading" && (
              <AccountTradingTabs
                key={id}
                userID={id}
                brokerAccountID={account.broker_account_id}
                currency={search.currency}
                live={live}
                section={search.section}
                page={search.page}
                onSectionChange={(section) => setSearch({ section })}
                onPageChange={(page) => navigate({ search: (prev) => ({ ...prev, page }), replace: true })}
              />
            )}
            {search.tab === "balances" && (
              <AccountBalancesTab
                key={id}
                account={account}
                currencies={currencies}
                currency={search.currency}
                live={live}
                page={search.page}
                onPageChange={(page) => navigate({ search: (prev) => ({ ...prev, page }), replace: true })}
                permissions={permissions}
              />
            )}
            {search.tab === "runs" && (
              <AccountRunsTab
                key={id}
                userID={id}
                brokerAccountID={account.broker_account_id}
                currency={search.currency}
                live={live}
                filter={search.runs}
                page={search.page}
                onFilterChange={(runs) => setSearch({ runs })}
                onPageChange={(page) => navigate({ search: (prev) => ({ ...prev, page }), replace: true })}
              />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}

function accountName(displayName: string | undefined, email: string | undefined, id: string): string {
  return displayName || email || id;
}

function HeaderCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-line-1 px-3 py-2">
      <dt className="text-2xs uppercase tracking-widest t-mute">{label}</dt>
      <dd className="mt-1 text-t-1">{children}</dd>
    </div>
  );
}

/**
 * The page-level currency strip: "All" plus every currency the SERVER
 * listed for this account, in registry order. Data-driven — a fourth
 * currency appears here with no console change — and view-only: it
 * narrows what is shown, it never totals anything.
 */
function CurrencyFilterTabs({
  value,
  currencies,
  onChange,
}: {
  value: CurrencyFilter;
  currencies: readonly string[];
  onChange: (next: CurrencyFilter) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("accountDetail.currencyFilterAria")}>
      <button
        type="button"
        role="tab"
        aria-selected={value === ALL_CURRENCIES}
        className={`page-tab ${value === ALL_CURRENCIES ? "is-active" : ""}`}
        onClick={() => onChange(ALL_CURRENCIES)}
      >
        {t("accountDetail.allCurrencies")}
      </button>
      {currencies.map((code) => (
        <button
          key={code}
          type="button"
          role="tab"
          aria-selected={value === code}
          className={`page-tab ${value === code ? "is-active" : ""}`}
          onClick={() => onChange(code)}
        >
          <CurrencyBadge currency={code} />
        </button>
      ))}
    </div>
  );
}
